import { serviceGo } from '@/utils/request'

export interface AuthorEarningsItem {
  author_id: number
  author_name: string
  total_books: number
  total_word_count: number
  total_earnings: number
  last_updated: string
}

export interface AuthorEarningsPagination {
  page: number
  page_size: number
  total_count: number
  total_pages: number
}

export function getAuthorEarnings(params: { page: number; page_size: number; keyword?: string }) {
  return serviceGo.get('/api_v2/admin/author-earnings', { params })
}

// ---- 按小说维度的收益（一行一本书）----

export interface BookEarningsCell {
  words: number
  earnings: number
}

// 主表里一本书挂着的作者。profile_filled 让管理员一眼看出
// 「这本书要打款但作者资料还没录」，不用逐个点开查。
export interface BookAuthorRef {
  author_id: number
  name: string
  profile_filled: boolean
}

export interface BookEarningsRow {
  game_id: number
  title: string
  author_names: string[]          // 一本书可能挂多个作者；没配作者时为空数组
  authors: BookAuthorRef[]        // 同上，但带 ID 与资料录入状态
  pen_name: string                // 书级笔名（对外署名），可能为空
  price_per_thousand_words: number
  price_is_default: boolean       // 未设定单价、按 80 兜底，界面上要标出来
  this_month: BookEarningsCell
  last_month: BookEarningsCell
}

// 账期本身由后端推导下发。账期规则随时间变过（2026-07 起才是自然月，
// 此前是「上月10日~本月9日」，中间夹了过渡周期），前端不重算，避免两边不一致。
export interface BookEarningsMonthMeta {
  bill_start: string
  bill_end: string
  label: string
}

export interface BookEarningsResponse {
  list: BookEarningsRow[]
  this_month: BookEarningsMonthMeta
  last_month: BookEarningsMonthMeta
  this_month_total: number
  last_month_total: number
}

export interface BookChapterEarning {
  chapter_id: number
  chapter_title: string
  word_count: number
  earnings: number
}

export interface BookEarningsPeriod {
  bill_start: string
  bill_end: string
  label: string
  total_words: number
  total_earnings: number
  chapters: BookChapterEarning[]
}

export interface BookEarningsDetail {
  game_id: number
  price_per_thousand_words: number
  periods: BookEarningsPeriod[]
}

function unwrap<T>(res: { data: { code: number; message: string; data: T } }): T {
  const body = res.data
  if (!body || typeof body !== 'object' || !('data' in body)) throw new Error('响应格式异常')
  if (body.code !== 0) throw new Error(body.message || '请求失败')
  return body.data
}

// keyword 同时匹配书名与作者名。不分页：全库只有十几本书。
export async function getBookEarnings(keyword?: string): Promise<BookEarningsResponse> {
  const params: Record<string, unknown> = {}
  if (keyword) params.keyword = keyword
  return unwrap(await serviceGo.get('/api_v2/admin/book-earnings', { params }))
}

// 展开某一行时才拉：历史账期 + 每个账期结算了哪几章
export async function getBookEarningsDetail(gameId: number): Promise<BookEarningsDetail> {
  return unwrap(await serviceGo.get('/api_v2/admin/book-earnings/detail', {
    params: { game_id: gameId },
  }))
}

// ---- 作者个人信息（打款用，仅管理员）----
//
// 含身份证号、银行卡号。列表接口默认返回脱敏值；
// 要看完整号码必须显式 reveal，且后端会写日志留痕。

export interface AuthorProfileRow {
  author_id: number
  username: string
  book_count: number
  has_data: boolean       // 是否录过（区分「没录」与「录了但某几项空着」）

  real_name: string
  id_card: string
  phone: string
  bank_card: string
  bank_name: string
  xingguo_id: string
  note: string

  masked: boolean         // true 表示上面的号码是脱敏值，不要拿去比对或复制
  updated_at: string

  // 应付稿费：该作者名下所有书在该账期的稿费之和。打款按人不按书，这两列才是实际要转的钱。
  this_month_earnings: number
  last_month_earnings: number
  has_multi_author_book: boolean  // 名下有书挂了多个作者，金额被重复计入
}

// 一本书挂了多个作者。业务上一本书只应有一个作者，出现即数据错误。
export interface MultiAuthorBookWarn {
  book_id: number
  title: string
  authors: string[]
  last_month: number
  overcounted: number   // 因重复归属虚增的金额 = 稿费 ×(作者数-1)
}

// 关联指向了已不存在的书（书删了但 book_author 没清）
export interface OrphanBookWarn {
  book_id: number
  authors: string[]
}

export interface AuthorProfileListResp {
  list: AuthorProfileRow[]
  total: number
  filled: number          // 已录入人数
  masked: boolean
  this_month_total: number
  last_month_total: number
  multi_author_books: MultiAuthorBookWarn[]
  orphan_books: OrphanBookWarn[]
}

// reveal=true 时返回完整号码，后端会记录「谁在什么时候看了」
export async function getAuthorProfiles(keyword?: string, reveal = false): Promise<AuthorProfileListResp> {
  const params: Record<string, unknown> = {}
  if (keyword) params.keyword = keyword
  if (reveal) params.reveal = 1
  return unwrap(await serviceGo.get('/api_v2/admin/author-profiles', { params }))
}

// 编辑弹窗回填用：必然返回明文，同样会留痕
export async function getAuthorProfile(authorId: number): Promise<{ found: boolean; profile: AuthorProfileRow }> {
  return unwrap(await serviceGo.get('/api_v2/admin/author-profile', { params: { author_id: authorId } }))
}

// 同一作者重复提交是覆盖，不会新增第二份 —— 否则打款时会有两条卡号打架
export async function saveAuthorProfile(payload: {
  author_id: number
  real_name: string
  id_card: string
  phone: string
  bank_card: string
  bank_name: string
  xingguo_id: string
  note: string
}): Promise<void> {
  await serviceGo.post('/api_v2/admin/author-profile', payload)
}

// ---- 作者收益下钻 ----

export interface ChapterEarningDetail {
  chapter_id: number
  chapter_title: string
  word_count: number
  gao_fei: number
  estimated: number
}

export interface BookEarningDetail {
  game_id: number
  title: string
  word_count: number
  price_per_thousand_words: number
  gao_fei: number
  rev_share: number
  estimated: number
  chapters: ChapterEarningDetail[]
}

export interface MonthlyEarnings {
  month: string
  total_words: number
  total_gao_fei: number
  total_rev_share: number
  total_estimated: number
  books: BookEarningDetail[]
}

export interface MonthDetail {
  month: string
  word_count: number
  gao_fei: number
  rev_share: number
  estimated: number
}

export interface BookEarnings {
  game_id: number
  title: string
  cover_url: string
  price_per_thousand_words: number
  total_words: number
  total_gao_fei: number
  total_rev_share: number
  total_estimated: number
  months: MonthDetail[]
}

export interface EarningsSummary {
  total_estimated_earnings: number
  total_word_count: number
  total_books: number
}

export function getAuthorEarningsSummary(authorId: number) {
  return serviceGo.get('/api_v2/earnings/summary', { params: { author_id: authorId } })
}

export function getAuthorEarningsByMonth(authorId: number) {
  return serviceGo.get('/api_v2/earnings/by_month', { params: { author_id: authorId } })
}

export function getAuthorEarningsByBook(authorId: number) {
  return serviceGo.get('/api_v2/earnings/by_book', { params: { author_id: authorId } })
}
